import type { CoverEffect, CoverEffectType } from 'fym-shared/src/music24/coverDoc';

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
const mix = (from: number, to: number, share: number): number => from + (to - from) * clamp01(share);

/**
 * The numbers of each effect from its strength (0..1). All sizes are shares of the cover side, so
 * the screen and the 3000 px file are the same picture. Pure.
 */
export const effectParams = {
  /** The alpha of the noise layer. */
  grain: (strength: number) => ({ alpha: mix(0.05, 0.6, strength), cellShare: 1 / 900 }),
  /** The alpha of the dark edge, and where the edge starts (a share of the radius). */
  vignette: (strength: number) => ({ alpha: mix(0.15, 0.92, strength), innerShare: mix(0.7, 0.4, strength) }),
  /** The count of the rings, their width and their alpha. */
  circles: (strength: number) => ({
    rings: Math.round(mix(3, 9, strength)),
    gapShare: mix(0.11, 0.055, strength),
    lineShare: 0.003,
    alpha: mix(0.2, 0.6, strength),
  }),
  /** The inset of the frame, the width of its line, its glow and its alpha. */
  neonFrame: (strength: number) => ({
    insetShare: 0.055,
    lineShare: mix(0.004, 0.009, strength),
    glowShare: mix(0.012, 0.045, strength),
    radiusShare: 0.012,
    alpha: mix(0.55, 1, strength),
  }),
  /** The alpha of the two lights and their size. */
  lightLeak: (strength: number) => ({ alpha: mix(0.2, 0.95, strength), radiusShare: mix(0.6, 0.95, strength) }),
  /** The size of one dot cell and the alpha of the dots. */
  halftone: (strength: number) => ({ cellShare: mix(0.012, 0.03, strength), alpha: mix(0.25, 0.8, strength) }),
  /** How much of the two-colour picture shows. */
  duotone: (strength: number) => ({ alpha: clamp01(strength) }),
  /** How many times smaller the picture is drawn before it is drawn large again. */
  blur: (strength: number) => ({ factor: mix(1, 28, strength), passes: strength > 0.5 ? 3 : 2 }),
} satisfies Record<CoverEffectType, (strength: number) => Record<string, number>>;

/** The colour `index` of an effect, or the fallback when the effect has fewer colours. Pure. */
export function effectColour(effect: Pick<CoverEffect, 'colours'>, index: number, fallback: string): string {
  return effect.colours[index] ?? fallback;
}

/** `#rrggbb` as three numbers 0..255. A text that is not a colour gives black. Pure. */
export function hexToRgb(hex: string): [number, number, number] {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (match === null) {
    return [0, 0, 0];
  }
  return [parseInt(match[1] ?? '0', 16), parseInt(match[2] ?? '0', 16), parseInt(match[3] ?? '0', 16)];
}

/** `rgba(r, g, b, a)` of a `#rrggbb` colour. Pure. */
export function rgba(hex: string, alpha: number): string {
  const [red, green, blue] = hexToRgb(hex);
  return `rgba(${red}, ${green}, ${blue}, ${clamp01(alpha)})`;
}

/** The luminance (0..1) of a pixel, by the Rec. 601 weights. Pure. */
export function luminance(red: number, green: number, blue: number): number {
  return (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
}
